import { invoke } from '@shared/easyfy.decorators';
import { globalEvent } from '@shared/easyfy.core';

@invoke
export default class QuitNicotineSavingCalculatorComponent {
  constructor() {
    globalEvent({
      type: 'click',
      events: [
        {
          action: 'change-quit-option',
          callback: this.onTabClick,
        },
        {
          action: 'calculate-savings',
          callback: this.onCalculate,
        },
      ],
    });
  }

  private onTabClick = (e: Event) => {
    const container = this.getContainer(e);
    if (!container) return;

    const target = e.target;
    if (!(target instanceof HTMLElement)) return;

    const tab = target.getAttribute('data-tab') || 'planning';
    QuitNicotineSavingCalculatorComponent.switchTab(tab, container);
  };

  private onCalculate = (e: Event) => {
    const container = this.getContainer(e);
    if (!container) return;

    QuitNicotineSavingCalculatorComponent.calculate(container);
  };

  private getContainer(e: Event) {
    const btn = e.target;
    if (!(btn instanceof HTMLElement)) return null;
    return btn.closest<HTMLElement>('.quit-nicotine-calculator');
  }

  private static switchTab(tab: string, container: HTMLElement) {
    container.querySelectorAll('.tab-button').forEach((btn) => {
      btn.classList.toggle('active', btn.getAttribute('data-tab') === tab);
    });

    this.toggleWeeksField(tab, container);
    this.hideResults(container);
  }

  private static toggleWeeksField(tab: string, container: HTMLElement) {
    const weeksField = container.querySelector<HTMLElement>('[data-container="weeks-wrapper"]');
    if (!weeksField) return;

    const isStopped = tab === 'stopped';
    weeksField.style.display = isStopped ? 'block' : 'none';
  }

  private static getCultureAndCurrency(): { culture: string; currency: string } {
    const htmlElement = document.documentElement;
    const culture = htmlElement.getAttribute('data-culture') || 'sv-SE';
    const currency = htmlElement.getAttribute('data-currency') || 'SEK';
    return { culture, currency };
  }

  private static formatCurrency(value: number): string {
    const { culture, currency } = this.getCultureAndCurrency();

    try {
      return new Intl.NumberFormat(culture, {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    } catch {
      return value.toString();
    }
  }

  private static calculate(container: HTMLElement) {
    const unitsInput = container.querySelector<HTMLInputElement>('[data-container="units-per-week"]');
    const priceInput = container.querySelector<HTMLInputElement>('[data-container="price-per-unit"]');
    const weeksInput = container.querySelector<HTMLInputElement>('[data-container="weeks-since-quit"]');

    if (!unitsInput || !priceInput || !weeksInput) return;

    const units = parseFloat(unitsInput.value) || 0;
    const price = parseFloat(priceInput.value) || 0;
    const weeks = parseFloat(weeksInput.value) || 0;

    const weekly = units * price;
    const weeklyRounded = Math.round(weekly);
    const monthly = Math.round(weekly * 4.28);
    const yearly = Math.round(weekly * 52.14);
    const totalSaved = Math.round(weekly * weeks);

    const isStoppedTab = this.isStoppedTab(container);

    if (isStoppedTab) {
      this.displayStoppedResults(container, weeklyRounded, monthly, totalSaved, weeks);
    } else {
      this.displayPlanningResults(container, monthly, yearly);
    }
  }

  private static isStoppedTab(container: HTMLElement): boolean {
    const activeTab = container.querySelector('.tab-button.active');
    return activeTab?.getAttribute('data-tab') === 'stopped';
  }

  private static displayStoppedResults(container: HTMLElement, weekly: number, monthly: number, totalSaved: number, weeks: number) {
    const resultContainer = container.querySelector<HTMLElement>('[data-container="result-planning"]');
    const savedContainer = container.querySelector<HTMLElement>('[data-container="result-saved"]');
    const savedAmountEl = container.querySelector<HTMLElement>('[data-container="saved-amount"]');
    const periodTextEl = container.querySelector<HTMLElement>('[data-container="period-text"]');
    const totalSavedAmount = container.querySelector<HTMLElement>('[data-container="total-saved-amount"]');

    resultContainer?.classList.add('hidden');

    if (!weeks || !savedContainer || !savedAmountEl || !periodTextEl || !totalSavedAmount) {
      savedContainer?.classList.add('hidden');
      return;
    }

    const isShortPeriod = weeks < 4;
    const displayAmount = isShortPeriod ? weekly : monthly;
    const periodTextAttr = isShortPeriod ? 'data-per-week-text' : 'data-per-month-text';
    const periodText = periodTextEl.getAttribute(periodTextAttr);

    savedAmountEl.innerText = this.formatCurrency(displayAmount);
    if (periodText) periodTextEl.innerText = periodText;
    totalSavedAmount.innerText = this.formatCurrency(totalSaved);
    savedContainer.classList.remove('hidden');
  }

  private static displayPlanningResults(container: HTMLElement, monthly: number, yearly: number) {
    const resultContainer = container.querySelector<HTMLElement>('[data-container="result-planning"]');
    const savedContainer = container.querySelector<HTMLElement>('[data-container="result-saved"]');
    const resultMonthly = container.querySelector<HTMLElement>('[data-container="monthly-amount"]');
    const resultYearly = container.querySelector<HTMLElement>('[data-container="yearly-amount"]');

    if (!resultMonthly || !resultYearly) return;

    resultMonthly.innerText = this.formatCurrency(monthly);
    resultYearly.innerText = this.formatCurrency(yearly);
    resultContainer?.classList.remove('hidden');
    savedContainer?.classList.add('hidden');
  }

  private static hideResults(container: HTMLElement) {
    container.querySelector('[data-container="result-planning"]')?.classList.add('hidden');
    container.querySelector('[data-container="result-saved"]')?.classList.add('hidden');
  }
}
